import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToSection } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import WorkspaceSwitcher from "@/components/nav/WorkspaceSwitcher";
import MissionControlButton from "@/components/nav/MissionControlButton";

const links = [
  { key: "features", href: "#features" },
  { key: "nicheFinder", href: "#niche" },
  { key: "tryBuilder", href: "#chat" },
  { key: "pricing", href: "#pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isAuthed } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 w-full z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => go("#top")} className="flex items-center gap-3" data-testid="logo-home">
            <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg shadow-[0_0_25px_rgba(0,85,255,0.6)]">
              A
            </div>
            <span className="font-display text-xl font-bold tracking-tight">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
          </button>
          {isAuthed && <div className="hidden lg:block"><WorkspaceSwitcher /></div>}
        </div>

        <div className="hidden lg:flex items-center gap-7 lg:mx-4 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="whitespace-nowrap hover:text-white transition-colors duration-300"
              data-testid={`nav-${l.key.toLowerCase()}`}
            >
              {t(`nav.${l.key}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>
          {isAuthed ? (
            <>
              <MissionControlButton variant="ghost" className="hidden lg:inline-flex" />
              <Link to="/studio" className="hidden lg:inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-[#00F0FF] hover:text-white transition-colors duration-300" data-testid="nav-studio">
                {t("common.studio")}
              </Link>
              <Link to="/account" className="hidden lg:inline-flex bg-white text-black px-5 py-2.5 rounded-full font-medium text-sm hover:bg-[#00F0FF] transition-colors duration-300" data-testid="nav-account">
                {t("common.account")}
              </Link>
            </>
          ) : (
            <>
              <Link to="/studio" className="hidden lg:inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-[#00F0FF] hover:text-white transition-colors duration-300" data-testid="nav-studio">
                {t("common.studio")}
              </Link>
              <Link to="/login" className="hidden lg:inline-flex font-mono text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors duration-300" data-testid="nav-login">
                {t("common.login")}
              </Link>
              <Link to="/signup" className="hidden lg:inline-flex bg-white text-black px-5 py-2.5 rounded-full font-medium text-sm hover:bg-[#00F0FF] transition-colors duration-300" data-testid="nav-cta">
                {t("common.startFree")}
              </Link>
            </>
          )}
          <button className="lg:hidden p-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu" aria-expanded={open}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5 font-mono text-sm uppercase tracking-widest">
              {links.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-slate-300 hover:text-white transition-colors" data-testid={`mobile-nav-${l.key.toLowerCase()}`}>
                  {t(`nav.${l.key}`)}
                </button>
              ))}
              <div className="h-px bg-white/10" />
              {isAuthed ? (
                <>
                  <Link to="/os" onClick={() => setOpen(false)} className="text-left text-[#00F0FF] hover:text-white transition-colors" data-testid="mobile-nav-mission-control">Mission Control</Link>
                  <Link to="/studio" onClick={() => setOpen(false)} className="text-left text-slate-300 hover:text-white transition-colors" data-testid="mobile-nav-studio">{t("common.studio")}</Link>
                  <Link to="/account" onClick={() => setOpen(false)} className="bg-white text-black px-5 py-3 rounded-full font-medium text-center" data-testid="mobile-nav-account">{t("common.account")}</Link>
                </>
              ) : (
                <>
                  <Link to="/studio" onClick={() => setOpen(false)} className="text-left text-[#00F0FF] hover:text-white transition-colors" data-testid="mobile-nav-studio">{t("common.studio")}</Link>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-left text-slate-300 hover:text-white transition-colors" data-testid="mobile-nav-login">{t("common.login")}</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="bg-white text-black px-5 py-3 rounded-full font-medium text-center" data-testid="mobile-nav-cta">{t("common.startFree")}</Link>
                </>
              )}
              <div className="pt-1"><LanguageSwitcher /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
